"use client";

import { Loader2, Save, X } from "lucide-react";

import { Button } from "@/components/ui/button";

/**
 * Sticky bottom save bar (Saleor-style).
 * Появляется когда есть несохранённые изменения, fixed внизу страницы.
 *
 * Usage:
 * const [dirty, setDirty] = useState(false);
 * <StickySaveBar
 *   visible={dirty}
 *   saving={mutation.isPending}
 *   onSave={() => mutation.mutate()}
 *   onDiscard={() => { resetForm(); setDirty(false); }}
 * />
 */
export function StickySaveBar({
  visible,
  saving = false,
  onSave,
  onDiscard,
  saveLabel = "Сохранить",
  discardLabel = "Отменить",
  message = "Есть несохранённые изменения",
  disabled = false,
}: {
  visible: boolean;
  saving?: boolean;
  onSave: () => void;
  onDiscard?: () => void;
  saveLabel?: string;
  discardLabel?: string;
  message?: string;
  disabled?: boolean;
}) {
  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
      <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{message}</p>
        <div className="flex gap-2">
          {onDiscard && (
            <Button
              variant="ghost"
              onClick={onDiscard}
              disabled={saving}
              className="gap-1.5"
            >
              <X className="h-4 w-4" />
              {discardLabel}
            </Button>
          )}
          <Button
            onClick={onSave}
            disabled={saving || disabled}
            className="gap-1.5"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saveLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
